import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Lock, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { PRESET_KEYS } from '@/features/staff/presetKeys';
import { useDeleteUser, useUpdateUser } from '@/api/hooks/useUsers';
import type { PermissionCatalog, User, UserRole } from '@/api/types';
import { useSession } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { cn, formatDate } from '@/lib/utils';

export function UserCard({ user, catalog }: { user: User; catalog: PermissionCatalog }) {
  const { t } = useTranslation();
  const { user: currentUser } = useSession();
  const isSelf = user.id === currentUser?.id;

  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<Record<string, boolean>>(user.permissions);
  const [dirty, setDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Re-sync the draft whenever the server copy changes underneath us (another
  // admin saved, or our own save just landed) — but never while there are
  // unsaved local edits, which would silently discard them.
  useEffect(() => {
    if (!dirty) setDraft(user.permissions);
  }, [user.permissions, dirty]);

  const allKeys = useMemo(() => catalog.categories.flatMap((c) => c.permissions.map((p) => p.key)), [catalog]);

  function toggle(key: string, checked: boolean) {
    if (isSelf && key === 'users.manage' && !checked) {
      toast({
        title: 'Cannot remove this permission',
        description: 'You cannot remove your own "Manage users" permission — you would be locked out.',
        variant: 'destructive',
      });
      return;
    }

    setDraft((prev) => ({ ...prev, [key]: checked }));
    setDirty(true);
  }

  function applyPreset(value: string) {
    let next: Record<string, boolean>;

    if (value === '__all') {
      next = Object.fromEntries(allKeys.map((k) => [k, true]));
    } else if (value === '__none') {
      next = Object.fromEntries(allKeys.map((k) => [k, false]));
    } else if (value === 'manager') {
      next = Object.fromEntries(allKeys.map((k) => [k, k !== 'users.manage']));
    } else {
      const grantedKeys = PRESET_KEYS[value];
      if (!grantedKeys) return;
      next = Object.fromEntries(allKeys.map((k) => [k, grantedKeys.includes(k)]));
    }

    if (isSelf) next = { ...next, 'users.manage': true };
    setDraft(next);
    setDirty(true);
  }

  async function handleSave() {
    try {
      await updateUser.mutateAsync({ id: user.id, request: { permissions: draft } });
      setDirty(false);
      toast({ title: 'Permissions saved', variant: 'success' });
    } catch (error) {
      toast({
        title: 'Could not save',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleRoleChange(role: UserRole) {
    try {
      await updateUser.mutateAsync({ id: user.id, request: { role } });
      toast({ title: 'Role updated', variant: 'success' });
    } catch (error) {
      toast({
        title: 'Could not change role',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleActiveToggle(isActive: boolean) {
    try {
      await updateUser.mutateAsync({ id: user.id, request: { isActive } });
      toast({ title: isActive ? 'User activated' : 'User deactivated', variant: 'success' });
    } catch (error) {
      toast({
        title: 'Could not update',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleDelete() {
    try {
      await deleteUser.mutateAsync(user.id);
      toast({ title: 'User deleted', variant: 'success' });
      setDeleteOpen(false);
    } catch (error) {
      toast({
        title: 'Could not delete',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-grad-primary text-xs font-semibold text-white">
          {user.name.slice(0, 2).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-medium">{user.name}</p>
            {isSelf && <Badge variant="secondary">You</Badge>}
            {dirty && <Badge variant="low">Unsaved</Badge>}
          </div>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </div>

        <Select
          value={user.role}
          onValueChange={(v) => handleRoleChange(v as UserRole)}
          disabled={isSelf || user.role === 'Superadmin'}
        >
          <SelectTrigger className="h-8 w-28 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Admin">{t('admin')}</SelectItem>
            <SelectItem value="Staff">{t('staff')}</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={user.isActive}
            onCheckedChange={handleActiveToggle}
            disabled={isSelf}
            aria-label={user.isActive ? 'Deactivate user' : 'Activate user'}
          />
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-status-expired disabled:opacity-30"
            disabled={isSelf}
            onClick={() => setDeleteOpen(true)}
            aria-label={`Delete ${user.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setExpanded((v) => !v)} aria-label="Toggle permissions">
            <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-secondary/30 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              {user.lastLoginAt ? `Last login ${formatDate(user.lastLoginAt)}` : 'Never logged in'}
            </div>
            <div className="flex items-center gap-2">
              <Select onValueChange={applyPreset}>
                <SelectTrigger className="h-8 w-44">
                  <SelectValue placeholder="Apply a preset…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Grant all</SelectItem>
                  <SelectItem value="__none">Revoke all</SelectItem>
                  {catalog.presets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id} title={preset.description}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleSave} disabled={!dirty || updateUser.isPending}>
                <Save className="h-3.5 w-3.5" /> Save
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {catalog.categories.map((category) => {
              const checkedCount = category.permissions.filter((p) => draft[p.key]).length;
              return (
                <div key={category.id}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-xs font-semibold">
                      {category.icon} {category.label}
                    </p>
                    <span className="tabular text-xs text-muted-foreground">
                      {checkedCount} of {category.permissions.length}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {category.permissions.map((perm) => {
                      const locked = isSelf && perm.key === 'users.manage';
                      return (
                        <label
                          key={perm.key}
                          className={cn(
                            'flex items-start gap-2 rounded-md px-1.5 py-1 text-xs',
                            !locked && 'cursor-pointer hover:bg-secondary',
                          )}
                          title={perm.description}
                        >
                          <input
                            type="checkbox"
                            checked={draft[perm.key] ?? false}
                            disabled={locked}
                            onChange={(e) => toggle(perm.key, e.target.checked)}
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary disabled:opacity-50"
                          />
                          <span className={cn('flex items-center gap-1', locked && 'text-muted-foreground')}>
                            {perm.label}
                            {locked && <Lock className="h-2.5 w-2.5" />}
                            {perm.sensitive && (
                              <span className="text-status-low" title="Sensitive — financial or destructive">
                                *
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${user.name}?`}
        description="This can't be undone. They'll lose access immediately."
        confirmLabel="Delete"
        pending={deleteUser.isPending}
        onConfirm={handleDelete}
      />
    </Card>
  );
}
